import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  DataGrid,
  GridToolbar
} from '@mui/x-data-grid';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Chip, 
  Button, 
  TextField, 
  InputAdornment,
  Badge,
  Stack,
  Alert,
  CircularProgress,
  ThemeProvider,
  createTheme,
  Tooltip
} from '@mui/material';
import { Download as DownloadIcon, ModeCommentOutlined as ModeCommentOutlinedIcon } from '@mui/icons-material';
import { iniciativasAPI } from '../services/api';
import './TablaDesarrolloNegocio.css';

const TablaDesarrolloNegocio = ({ 
  embedded = false, 
  añosFiltro = null,
  searchTerm = '',
  estadosFiltro = [],
  areasFiltro = [],
  jefesFiltro = [],
  negocioFiltro = [],
  responsablesDHFiltro = [],
  probabilidadFiltro = [],
  desvioFiltroProp = null,
  iniciativas: iniciativasProp = null,
  loading: loadingProp = false,
  totalIniciativas = null 
}) => {
  const [iniciativas, setIniciativas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const scrollRef = useRef(null);
  const gridRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [desvioFiltro, setDesvioFiltro] = useState([]);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  // Funciones para manejo de localStorage de columnas
  const getColumnVisibilityFromLocalStorage = () => {
    try {
      const saved = localStorage.getItem('desarrollo_negocio_column_visibility');
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error('Error loading column visibility from localStorage:', error);
      return {};
    }
  };

  const saveColumnVisibilityToLocalStorage = (visibilityModel) => {
    try {
      localStorage.setItem('desarrollo_negocio_column_visibility', JSON.stringify(visibilityModel));
    } catch (error) {
      console.error('Error saving column visibility to localStorage:', error);
    }
  };

  // Cargar visibilidad de columnas al inicializar
  useEffect(() => {
    const savedVisibility = getColumnVisibilityFromLocalStorage();
    setColumnVisibilityModel(savedVisibility);
  }, []);

  // Función para manejar cambios en visibilidad de columnas
  const handleColumnVisibilityModelChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    saveColumnVisibilityToLocalStorage(newModel);
  };

  // Función para restablecer visibilidad de columnas
  const resetColumnVisibility = () => {
    const defaultVisibility = {};
    setColumnVisibilityModel(defaultVisibility);
    saveColumnVisibilityToLocalStorage(defaultVisibility);
  };

  // Usar filtro de desvío desde props si está disponible (cuando está embebido)
  const desvioFiltroActivo = embedded && desvioFiltroProp !== null ? desvioFiltroProp : desvioFiltro;

  // Si se pasan iniciativas desde props, usarlas directamente (ya están filtradas)
  const usarIniciativasProp = Boolean(iniciativasProp);

  // Usar iniciativas de props si están disponibles
  useEffect(() => {
    if (usarIniciativasProp && iniciativasProp) {
      // Filtrar iniciativas de tipo genérica
      const iniciativasFiltradas = iniciativasProp.filter(i => i.tipo_de_iniciativa !== 'generica');
      setIniciativas(iniciativasFiltradas);
      setLoading(loadingProp);
      setError(null);
    }
  }, [iniciativasProp, loadingProp, usarIniciativasProp]);

  // Obtener filtro de estado desde URL
  const getFilterFromURL = () => {
    const params = new URLSearchParams(location.search);
    return params.get('filtro');
  };

  // Función para obtener el display del filtro activo
  const getFilterDisplay = (estado) => {
    const estadoMap = {
      'oportunidad_identificada': 'Identificada',
      'en_preparacion_propuesta': 'En preparación',
      'validacion_lider_tecnico': 'Validación Líder Técnico',
      'supervision': 'Supervisión',
      'entregada': 'Entregada',
      'aprobado': 'Aprobado',
      'planificada': 'Planificada',
      'en_curso': 'En Curso',
      'bloqueada': 'Bloqueada',
      'finalizada': 'Finalizada',
      'cancelada': 'Cancelada',
      'rechazada': 'Rechazada'
    };
    return estadoMap[estado] || estado;
  };

  // Función para limpiar filtro
  const clearFilter = () => {
    navigate(location.pathname);
  };

  // Hook para manejar el scroll horizontal con arrastre
  useEffect(() => {
    const handleMouseDown = (e) => {
      if (!scrollRef.current) return;
      setIsDragging(true);
      setStartX(e.pageX - scrollRef.current.offsetLeft);
      setScrollLeft(scrollRef.current.scrollLeft);
      scrollRef.current.style.cursor = 'grabbing';
      scrollRef.current.style.userSelect = 'none';
    };

    const handleMouseLeave = () => {
      if (!scrollRef.current) return;
      setIsDragging(false);
      scrollRef.current.style.cursor = 'grab';
      scrollRef.current.style.userSelect = 'auto';
    };

    const handleMouseUp = () => {
      if (!scrollRef.current) return;
      setIsDragging(false);
      scrollRef.current.style.cursor = 'grab';
      scrollRef.current.style.userSelect = 'auto';
    };

    const handleMouseMove = (e) => {
      if (!isDragging || !scrollRef.current) return;
      e.preventDefault();
      const x = e.pageX - scrollRef.current.offsetLeft;
      const walk = (x - startX) * 2; // Multiplicador para velocidad de scroll
      scrollRef.current.scrollLeft = scrollLeft - walk;
    };

    const scrollElement = scrollRef.current;
    if (scrollElement) {
      scrollElement.addEventListener('mousedown', handleMouseDown);
      scrollElement.addEventListener('mouseleave', handleMouseLeave);
      scrollElement.addEventListener('mouseup', handleMouseUp);
      scrollElement.addEventListener('mousemove', handleMouseMove);

      // Prevenir el comportamiento por defecto de arrastre en enlaces e imágenes
      scrollElement.addEventListener('dragstart', (e) => e.preventDefault());

      return () => {
        scrollElement.removeEventListener('mousedown', handleMouseDown);
        scrollElement.removeEventListener('mouseleave', handleMouseLeave);
        scrollElement.removeEventListener('mouseup', handleMouseUp);
        scrollElement.removeEventListener('mousemove', handleMouseMove);
        scrollElement.removeEventListener('dragstart', (e) => e.preventDefault());
      };
    }
  }, [isDragging, startX, scrollLeft]);

  // Tema personalizado para integración con Bootstrap
  const theme = createTheme({
    palette: {
      primary: {
        main: '#0d6efd', // Bootstrap primary color
      },
      secondary: {
        main: '#6c757d', // Bootstrap secondary color
      },
    },
    components: {
      MuiDataGrid: {
        styleOverrides: {
          columnHeaders: {
            backgroundColor: 'white !important',
            color: 'black !important',
            fontWeight: 'bold !important',
            borderBottom: '2px solid #dee2e6 !important',
            '& .MuiDataGrid-columnHeaderTitle': {
              color: 'black !important',
              fontWeight: 'bold !important',
            },
            '& .MuiDataGrid-iconButtonContainer': {
              color: 'black !important',
            },
            '& .MuiDataGrid-menuIcon': {
              color: 'black !important',
            },
            '& .MuiDataGrid-sortIcon': {
              color: 'black !important',
            },
            '& .MuiDataGrid-filterIcon': {
              color: 'black !important',
            },
            '& .MuiDataGrid-columnSeparator': {
              color: '#dee2e6 !important',
            }
          },
          columnHeader: {
            backgroundColor: 'white !important',
            color: 'black !important',
            '& .MuiDataGrid-columnHeaderTitle': {
              color: 'black !important',
              fontWeight: 'bold !important',
            },
            '& .MuiDataGrid-iconButtonContainer': {
              color: 'black !important',
            },
            '& .MuiDataGrid-menuIcon': {
              color: 'black !important',
            },
            '& .MuiDataGrid-sortIcon': {
              color: 'black !important',
            },
            '& .MuiDataGrid-filterIcon': {
              color: 'black !important',
            }
          },
          columnHeaderTitle: {
            color: 'black !important',
            fontWeight: 'bold !important',
          }
        }
      }
    }
  });

  // Cargar todas las iniciativas al montar el componente
  // Solo si no se están usando las props de iniciativas filtradas
  useEffect(() => {
    if (!usarIniciativasProp) {
      cargarIniciativas();
    }
  }, [usarIniciativasProp]);

  const cargarIniciativas = async () => {
    try {
      setLoading(true);
      const response = await iniciativasAPI.getTablaDesarrolloNegocio();
      console.log('Respuesta API:', response.data);
      
      // Filtrar iniciativas de tipo genérica
      const iniciativasFiltradas = Array.isArray(response.data) 
        ? response.data.filter(i => i.tipo_de_iniciativa !== 'generica')
        : [];
      setIniciativas(iniciativasFiltradas);
      setError(null);
    } catch (err) {
      console.error('Error cargando iniciativas:', err);
      setError('Error al cargar las iniciativas');
    } finally {
      setLoading(false);
    }
  };

  // Función para formatear valores decimales
  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: true
    }).format(value);
  };

  // Función para formatear fechas
  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('es-ES');
  };

  // Función para obtener el color del chip de estado
  const getEstadoColor = (estado) => {
    if (['finalizada', 'aprobado', 'aprobada'].includes(estado)) return 'success';
    if (['en_curso', 'planificada'].includes(estado)) return 'primary';
    if (['rechazada', 'cancelada'].includes(estado)) return 'error';
    if (['propuesta', 'validacion_lider_tecnico'].includes(estado)) return 'warning';
    return 'default';
  };

  // Función para obtener el color del chip de probabilidad
  const getProbabilidadColor = (probabilidad) => {
    if (probabilidad === 'alta') return 'success';
    if (probabilidad === 'media') return 'warning';
    if (probabilidad === 'baja') return 'error';
    return 'default';
  };

  // Función para obtener el texto del estado
  const getEstadoDisplay = (estado) => {
    const estadoMap = {
      'oportunidad_identificada': 'Identificada',
      'en_preparacion_propuesta': 'En preparación',
      'supervision': 'Supervisión',
      'entregada': 'Entregada',
      'rechazada': 'Rechazada',
      'stand_by': 'Stand-By',
      'aprobado': 'Aprobado',
      'aprobada': 'Aprobada',
      'planificada': 'Planificada',
      'en_curso': 'En curso',
      'bloqueada': 'Bloqueada',
      'cancelada': 'Cancelada',
      'finalizada': 'Finalizada',
    };
    return estadoMap[estado] || estado;
  };

  // Función para obtener el texto de probabilidad
  const getProbabilidadDisplay = (probabilidad) => {
    const probMap = {
      'alta': 'Alta',
      'media': 'Media',
      'baja': 'Baja',
    };
    return probMap[probabilidad] || probabilidad || '-';
  };

  // Literal de comentario automático de Azure DevOps
  const COMENTARIO_AUTO_AZDEVOPS = '📝 Este comentario fue generado automáticamente durante la importación desde Azure DevOps.';

  // Función para construir el texto del tooltip del último comentario
  const getUltimoComentarioTooltip = (iniciativa) => {
    if (!iniciativa.ultimo_comentario) {
      return 'No se ha registrado ningún comentario';
    }
    const { autor_nombre, fecha_creacion, contenido } = iniciativa.ultimo_comentario;
    const fecha = new Date(fecha_creacion).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
    const esAutoGenerado = contenido && contenido.includes(COMENTARIO_AUTO_AZDEVOPS);
    const textoMostrar = esAutoGenerado ? COMENTARIO_AUTO_AZDEVOPS : contenido;
    return `${autor_nombre || 'Desconocido'} · ${fecha}\n${textoMostrar}`;
  };

  // Devuelve true si hay un comentario humano en los últimos 7 días
  const tieneComentarioReciente = (iniciativa) => {
    if (!iniciativa.ultimo_comentario || !iniciativa.tiene_comentarios_humanos) return false;
    const diffMs = Date.now() - new Date(iniciativa.ultimo_comentario.fecha_creacion).getTime();
    return diffMs < 7 * 24 * 60 * 60 * 1000;
  };

  // Función para obtener el año de la iniciativa
  const getAñoIniciativa = (iniciativa) => {
    return iniciativa.ano || null;
  };

  // Función para calcular el estado del desvío
  const calcularDesvio = (iniciativa) => {
    // El desvío solo aplica cuando el estado es "en_preparacion_propuesta"
    if (iniciativa.estado !== 'en_preparacion_propuesta') {
      return { 
        color: 'default', 
        tooltip: 'Desvío no aplica (estado diferente a En preparación)',
        valor: 0 // Para ordenamiento: gris = 0
      };
    }

    const fechaPlanificada = iniciativa.fecha_de_entrega_propuesta_prevista;
    const fechaReal = iniciativa.fecha_de_entrega_propuesta_real;

    // Si no hay fecha planificada o ya se entregó, gris
    if (!fechaPlanificada || fechaReal) {
      return { 
        color: 'default', 
        tooltip: fechaReal ? 'Propuesta entregada' : 'Sin fecha planificada',
        valor: 0 // Para ordenamiento: gris = 0
      };
    }

    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    
    const fechaPlanificadaDate = new Date(fechaPlanificada);
    fechaPlanificadaDate.setHours(0, 0, 0, 0);

    const diasDiferencia = Math.ceil((fechaPlanificadaDate - hoy) / (1000 * 60 * 60 * 24));

    if (diasDiferencia < 0) {
      // Ya pasó la fecha planificada
      const diasRetraso = Math.abs(diasDiferencia);
      return { 
        color: 'error', 
        tooltip: `Retrasado ${diasRetraso} día${diasRetraso !== 1 ? 's' : ''}`,
        valor: 3 // Para ordenamiento: rojo = 3
      };
    } else if (diasDiferencia <= 3) {
      // Entre 0 y 3 días (cerca de la fecha)
      return { 
        color: 'warning', 
        tooltip: diasDiferencia === 0 
          ? 'Entrega hoy' 
          : `${diasDiferencia} día${diasDiferencia !== 1 ? 's' : ''} para entrega`,
        valor: 2 // Para ordenamiento: amarillo = 2
      };
    } else {
      // Más de 3 días
      return { 
        color: 'success', 
        tooltip: `${diasDiferencia} días para entrega`,
        valor: 1 // Para ordenamiento: verde = 1
      };
    }
  };

  // Aplicar filtro de desvío
  const aplicarFiltroDesvio = (iniciativasAFiltrar) => {
    // Si desvioFiltroActivo contiene '__NONE__', no mostrar nada
    if (desvioFiltroActivo.includes('__NONE__')) {
      return [];
    }
    
    // Si el array está vacío, mostrar todos
    if (desvioFiltroActivo.length === 0) {
      return iniciativasAFiltrar;
    }
    
    return iniciativasAFiltrar.filter(iniciativa => {
      const desvio = calcularDesvio(iniciativa);
      
      // Mapear color a valor de filtro
      let colorFiltro = '';
      switch(desvio.color) {
        case 'error': colorFiltro = 'rojo'; break;
        case 'warning': colorFiltro = 'amarillo'; break;
        case 'success': colorFiltro = 'verde'; break;
        case 'default': colorFiltro = 'gris'; break;
        default: return false;
      }
      
      return desvioFiltroActivo.includes(colorFiltro);
    });
  };

  // Las iniciativas ya vienen filtradas desde el componente padre cuando está embebido
  const iniciativasConFiltroDesvio = aplicarFiltroDesvio(iniciativas);
  const iniciativasParaRender = iniciativasConFiltroDesvio;

  // Función para calcular el tamaño de página dinámico
  const calcularPageSize = (totalResultados) => {
    if (totalResultados <= 10) return 10;
    if (totalResultados <= 25) return 25;
    if (totalResultados <= 50) return 50;
    if (totalResultados <= 100) return 100;
    return 100; // Para más de 100, usar 100 y que el usuario pagine
  };

  const pageSizeDinamico = calcularPageSize(iniciativasParaRender.length);

  // Configuración de columnas para el DataGrid
  const columns = [
    {
      field: 'desvio',
      headerName: 'Desvío',
      flex: 0.4,
      minWidth: 80,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (value, row) => {
        if (!row) return 0;
        const desvio = calcularDesvio(row);
        return desvio.valor;
      },
      sortComparator: (v1, v2) => v1 - v2,
      renderCell: (params) => {
        const desvio = calcularDesvio(params.row);
        return (
          <Tooltip title={desvio.tooltip} arrow>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                backgroundColor: 
                  desvio.color === 'error' ? '#d32f2f' :
                  desvio.color === 'warning' ? '#ed6c02' :
                  desvio.color === 'success' ? '#2e7d32' :
                  '#9e9e9e',
                display: 'inline-block'
              }}
            />
          </Tooltip>
        );
      }
    },
    {
      field: 'nombre_de_la_oportunidad',
      headerName: 'Nombre de la oportunidad',
      flex: 1.2,
      minWidth: 250,
      renderCell: (params) => (
        <Tooltip
          title={
            <span style={{ whiteSpace: 'pre-line', fontSize: '0.8rem' }}>
              {getUltimoComentarioTooltip(params.row)}
            </span>
          }
          arrow
          placement="right"
        >
          <Box 
            sx={{ 
              fontWeight: 'bold',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
            onClick={(e) => {
              e.stopPropagation(); // Prevenir que el click se propague a la fila
              handleRowClick(params);
            }}
          >
            <Link 
              to={`/projects/${params.row.id}?from=desarrollo_negocio`}
              style={{ 
                textDecoration: 'none',
                color: '#0d6efd',
                fontWeight: 'bold'
              }}
            >
              {params.value || '-'}
            </Link>
            {tieneComentarioReciente(params.row) && (
              <ModeCommentOutlinedIcon
                sx={{
                  fontSize: '14px',
                  color: '#7b9fd4',
                  flexShrink: 0,
                  opacity: 0.8,
                }}
              />
            )}
          </Box>
        </Tooltip>
      )
    },
    {
      field: 'estado',
      headerName: 'Estado',
      flex: 0.8,
      minWidth: 140,
      renderCell: (params) => (
        <Chip
          label={getEstadoDisplay(params.value)}
          color={getEstadoColor(params.value)}
          variant="filled"
          size="small"
        />
      )
    },
    {
      field: 'probabilidad_de_aprobacion',
      headerName: 'Probabilidad',
      flex: 0.6,
      minWidth: 120,
      renderCell: (params) => {
        // No mostrar probabilidad si el estado es aprobado, aprobada o rechazada
        if (params.row.estado === 'aprobado' || params.row.estado === 'aprobada' || params.row.estado === 'rechazada') {
          return '-';
        }
        return (
          <Chip
            label={getProbabilidadDisplay(params.value)}
            color={getProbabilidadColor(params.value)}
            variant="filled"
            size="small"
          />
        );
      }
    },
    {
      field: 'tecnologia_nombre',
      headerName: 'Área de conocimiento',
      flex: 0.8,
      minWidth: 150,
      renderCell: (params) => params.value || '-'
    },
    {
      field: 'subnegocio',
      headerName: 'Subnegocio',
      flex: 0.8,
      minWidth: 150,
      renderCell: (params) => params.row.negocio?.subnegocio || '-'
    },
    {
      field: 'responsable_dh',
      headerName: 'Responsable DN',
      flex: 0.7,
      minWidth: 140,
      renderCell: (params) => params.value || '-'
    },
    {
      field: 'importe_total_cliente',
      headerName: 'Importe de Venta',
      flex: 0.7,
      minWidth: 130,
      renderCell: (params) => formatCurrency(params.value)
    },
    {
      field: 'importe_total_estimado',
      headerName: 'Importe total estimado',
      flex: 0.7,
      minWidth: 130,
      renderCell: (params) => formatCurrency(params.value)
    },
    {
      field: 'fecha_de_entrega_propuesta_prevista',
      headerName: 'Fecha de entrega propuesta (prevista)',
      flex: 0.7,
      minWidth: 120,
      renderCell: (params) => formatDate(params.value)
    },
    {
      field: 'fecha_de_entrega_propuesta_real',
      headerName: 'Fecha entrega real',
      flex: 0.7,
      minWidth: 120,
      renderCell: (params) => formatDate(params.value)
    },
    {
      field: 'fecha_de_aprobacion_propuesta_firmada',
      headerName: 'Fecha de aprobación/propuesta firmada',
      flex: 0.7,
      minWidth: 130,
      renderCell: (params) => formatDate(params.value)
    },
    {
      field: 'updated_at',
      headerName: 'Última actualización',
      flex: 0.8,
      minWidth: 160,
      align: 'center',
      headerAlign: 'center',
      type: 'dateTime',
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: (params) => {
        if (!params.value) return '-';
        return params.value.toLocaleString('es-ES', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit'
        });
      }
    }
  ];

  const normalizeNumericValue = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;

    const raw = String(value).trim();
    if (!raw) return null;

    let cleaned = raw
      .replace(/\u00a0/g, '')
      .replace(/€/g, '')
      .replace(/%/g, '')
      .replace(/\s+/g, '');

    if (!/[0-9]/.test(cleaned)) return null;

    if (cleaned.includes(',') && cleaned.includes('.')) {
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else if (cleaned.includes(',')) {
      cleaned = cleaned.replace(',', '.');
    }

    const num = Number(cleaned);
    return Number.isFinite(num) ? num : null;
  };

  const formatNumericForCsv = (value) => {
    return new Intl.NumberFormat('es-ES', {
      useGrouping: false,
      minimumFractionDigits: 0,
      maximumFractionDigits: 10
    }).format(value);
  };

  const escapeCsvCell = (value) => {
    if (value === null || value === undefined) return '';
    const stringValue = String(value);
    if (/[;"\n\r]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const isColumnVisible = (field) => columnVisibilityModel[field] !== false;

  const getExportableColumns = () => {
    return columns.filter((column) => column.field && isColumnVisible(column.field));
  };

  const getExportCellValue = (row, field) => {
    switch (field) {
      case 'desvio':
        return calcularDesvio(row).tooltip || '';
      case 'nombre_de_la_oportunidad':
        return row.nombre_de_la_oportunidad || '';
      case 'estado':
        return getEstadoDisplay(row.estado) || '';
      case 'probabilidad_de_aprobacion':
        return (row.estado === 'aprobado' || row.estado === 'aprobada' || row.estado === 'rechazada')
          ? ''
          : (getProbabilidadDisplay(row.probabilidad_de_aprobacion) || '');
      case 'tecnologia_nombre':
        return row.tecnologia_nombre || '';
      case 'subnegocio':
        return row.negocio?.subnegocio || '';
      case 'responsable_dh':
        return row.responsable_dh || row.responsable_dn_nombre || '';
      case 'importe_total_cliente':
        return normalizeNumericValue(row.importe_total_cliente);
      case 'importe_total_estimado':
        return normalizeNumericValue(row.importe_total_estimado);
      case 'fecha_de_entrega_propuesta_prevista':
        return row.fecha_de_entrega_propuesta_prevista || '';
      case 'fecha_de_entrega_propuesta_real':
        return row.fecha_de_entrega_propuesta_real || '';
      case 'fecha_de_aprobacion_propuesta_firmada':
        return row.fecha_de_aprobacion_propuesta_firmada || '';
      case 'updated_at':
        return row.updated_at || '';
      default:
        return row[field] ?? '';
    }
  };

  const formatExportValueForCsv = (value) => {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'number') return formatNumericForCsv(value);
    return value;
  };

  // Manejo del click en la fila
  const handleRowClick = (params) => {
    // Solo navegar si no estamos arrastrando
    if (!isDragging) {
      navigate(`/projects/${params.id}?from=desarrollo_negocio`);
    }
  };

  if (loading) {
    return (
      <Box className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>Cargando...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" action={
        <Button color="inherit" size="small" onClick={cargarIniciativas}>
          Reintentar
        </Button>
      }>
        <Typography variant="h6">Error</Typography>
        {error}
      </Alert>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ 
        width: '100%', 
        maxWidth: '100vw', 
        overflow: 'hidden',
        px: embedded ? 0 : { xs: 1, sm: 2, md: 3 },
        py: embedded ? 0 : 2
      }}>
        {/* Header - Solo mostrar si no está embebido */}
        {!embedded && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 },
            mb: 4 
          }}>
            <Typography variant="h4" component="h2" sx={{ 
              mb: 0,
              fontSize: { xs: '1.5rem', sm: '2rem' }
            }}>
              <i className="fas fa-table text-primary me-2"></i>
              Tabla Desarrollo de Negocio
            </Typography>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              spacing={2} 
              alignItems={{ xs: 'stretch', sm: 'center' }}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Button
                variant="contained"
                color="success"
                onClick={() => navigate('/crear-oportunidad')}
                fullWidth={{ xs: true, sm: false }}
              >
                <i className="fas fa-plus me-2"></i>
                Crear Oportunidad
              </Button>
              <Badge badgeContent={iniciativasParaRender.length} color="primary">
                <Typography variant="body2">
                  iniciativa{iniciativasParaRender.length !== 1 ? 's' : ''}
                </Typography>
              </Badge>
            </Stack>
          </Box>
        )}

        {/* Buscador y Filtros */}
        <Box sx={{ mb: 3 }}>
          {/* Indicador de filtro activo */}
          {getFilterFromURL() && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                Filtrado por estado:
              </Typography>
              <Chip
                label={getFilterDisplay(getFilterFromURL())}
                color="primary"
                variant="outlined"
                onDelete={clearFilter}
                deleteIcon={<span style={{ fontSize: '14px' }}>✕</span>}
                size="small"
                sx={{ fontWeight: 'bold' }}
              />
              <Typography variant="caption" color="text.secondary">
                ({iniciativasParaRender.length} resultado{iniciativasParaRender.length !== 1 ? 's' : ''})
              </Typography>
            </Box>
          )}
          
        </Box>

        {/* DataGrid */}
        <Card sx={{ 
          width: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          height: { xs: 'calc(100vh - 200px)', sm: 'calc(100vh - 170px)', md: 'calc(100vh - 140px)' },
          boxShadow: 3,
          borderRadius: 2
        }}>
          <CardContent sx={{ 
            p: 0, 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* DataGrid with responsive scrolling */}
            <Box 
              sx={{ 
                flex: 1,
                width: '100%',
                minHeight: 0, // Important for flex shrinking
              }}
              ref={scrollRef}
              className={`mui-datagrid-container ${isDragging ? 'dragging' : ''}`}
            >
              <DataGrid
                ref={gridRef}
                rows={iniciativasParaRender}
                columns={columns}
                pinnedColumns={{ left: ['desvio', 'nombre_de_la_oportunidad'] }}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: pageSizeDinamico,
                    },
                  },
                  sorting: {
                    sortModel: [{ field: 'updated_at', sort: 'desc' }],
                  },
                  columns: {
                    columnVisibilityModel: columnVisibilityModel,
                  },
                }}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                checkboxSelection={false}
                disableRowSelectionOnClick={true}
                autoHeight={false}
                disableColumnSelector={false}
                disableColumnMenu={false}
                disableColumnSorting={false}
                sortingMode="client"
                slots={{
                  toolbar: GridToolbar,
                }}
                slotProps={{
                  toolbar: {
                    showQuickFilter: true,
                    quickFilterProps: { debounceMs: 500 },
                  },
                }}
                    sx={{
                      border: 'none',
                      height: '100%',
                      width: '100%',
                      '& .MuiDataGrid-main': {
                        overflow: 'auto'
                      },
                      '& .MuiDataGrid-virtualScroller': {
                        // Ensure proper scrolling behavior
                        overflowX: 'auto',
                        overflowY: 'auto'
                      },
                      '& .MuiDataGrid-cell': {
                        cursor: 'default', // Cursor normal para las celdas
                        textAlign: 'center !important',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      },
                      '& .MuiDataGrid-cell[data-field="nombre_de_la_oportunidad"]': {
                        textAlign: 'left !important',
                        justifyContent: 'flex-start'
                      },
                      '& .MuiDataGrid-row:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)', // Mantener hover visual
                      },
                      // Estilos para columnas fijas
                      '& .MuiDataGrid-cell--pinnedLeft': {
                        backgroundColor: 'white',
                        zIndex: 10,
                      },
                      '& .MuiDataGrid-cell--pinnedLeft:not(:last-of-type)::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '1px',
                        backgroundColor: 'rgba(224, 224, 224, 1)',
                      },
                      '& .MuiDataGrid-columnHeader--pinnedLeft': {
                        backgroundColor: 'white !important',
                        zIndex: 11,
                      },
                      '& .MuiDataGrid-columnHeader--pinnedLeft:not(:last-of-type)::after': {
                        content: '""',
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '1px',
                        backgroundColor: 'rgba(224, 224, 224, 1)',
                      },
                      '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: 'white !important',
                        color: 'black !important',
                        fontWeight: 'bold !important',
                        borderBottom: '2px solid #dee2e6 !important',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        '& .MuiDataGrid-columnHeaderTitle': {
                          color: 'black !important',
                          fontWeight: 'bold !important',
                        }
                      },
                      '& .MuiDataGrid-columnHeader': {
                        backgroundColor: 'white !important',
                        color: 'black !important',
                        '& .MuiDataGrid-columnHeaderTitle': {
                          color: 'black !important',
                          fontWeight: 'bold !important',
                        }
                      },
                      '& .MuiDataGrid-columnHeader .MuiDataGrid-columnSeparator': {
                        color: '#dee2e6 !important',
                      },
                      '& .MuiDataGrid-menuIcon': {
                        color: 'black !important',
                      },
                      '& .MuiDataGrid-sortIcon': {
                        color: 'black !important',
                      },
                      '& .MuiDataGrid-filterIcon': {
                        color: 'black !important',
                      },
                      '& .MuiDataGrid-iconButtonContainer': {
                        color: 'black !important',
                      },
                      '& .MuiDataGrid-columnHeaderTitleContainer': {
                        color: 'black !important',
                      },
                      // Estilos para la toolbar
                      '& .MuiDataGrid-toolbarContainer': {
                        padding: '8px',
                        backgroundColor: '#f8f9fa',
                        borderBottom: '1px solid #dee2e6',
                        '& .MuiButton-root': {
                          color: '#495057',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                          }
                        },
                        '& .MuiInputBase-root': {
                          backgroundColor: 'white',
                          '& .MuiInputBase-input': {
                            padding: '6px 8px',
                            fontSize: '0.875rem'
                          }
                        }
                      },
                      // Responsive behavior
                      '& .MuiDataGrid-cell--textLeft': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }
                    }}
                    localeText={{
                      // Textos en español
                      noRowsLabel: 'No se encontraron iniciativas',
                      noResultsOverlayLabel: 'No se encontraron resultados',
                      errorOverlayDefaultLabel: 'Ha ocurrido un error',
                      toolbarColumns: 'Columnas',
                      toolbarFilters: 'Filtros',
                      toolbarDensity: 'Densidad',
                      toolbarExport: 'Exportar',
                      columnsPanelTextFieldLabel: 'Encontrar columna',
                      columnsPanelTextFieldPlaceholder: 'Título de columna',
                      columnsPanelDragIconLabel: 'Reordenar columna',
                      columnsPanelShowAllButton: 'Mostrar todas',
                      columnsPanelHideAllButton: 'Ocultar todas',
                      filterPanelAddFilter: 'Añadir filtro',
                      filterPanelDeleteIconLabel: 'Borrar',
                      filterPanelOperators: 'Operadores',
                      filterPanelOperatorAnd: 'Y',
                      filterPanelOperatorOr: 'O',
                      filterPanelColumns: 'Columnas',
                      filterPanelInputLabel: 'Valor',
                      filterPanelInputPlaceholder: 'Valor del filtro',
                      filterOperatorContains: 'contiene',
                      filterOperatorEquals: 'es igual a',
                      filterOperatorStartsWith: 'comienza por',
                      filterOperatorEndsWith: 'termina en',
                      filterOperatorIsEmpty: 'está vacío',
                      filterOperatorIsNotEmpty: 'no está vacío',
                      columnMenuLabel: 'Menú',
                      columnMenuShowColumns: 'Mostrar columnas',
                      columnMenuFilter: 'Filtro',
                      columnMenuHideColumn: 'Ocultar',
                      columnMenuUnsort: 'Desordenar',
                      columnMenuSortAsc: 'Ordenar ASC',
                      columnMenuSortDesc: 'Ordenar DESC',
                      columnHeaderFiltersTooltipActive: (count) =>
                        count !== 1 ? `${count} filtros activos` : `${count} filtro activo`,
                      columnHeaderFiltersLabel: 'Mostrar filtros',
                      columnHeaderSortIconLabel: 'Ordenar',
                      footerRowSelected: (count) =>
                        count !== 1
                          ? `${count.toLocaleString('es-ES')} filas seleccionadas`
                          : `${count.toLocaleString('es-ES')} fila seleccionada`,
                      footerTotalRows: 'Filas totales:',
                      footerTotalVisibleRows: (visibleCount, totalCount) =>
                        `${visibleCount.toLocaleString('es-ES')} de ${totalCount.toLocaleString('es-ES')}`,
                      checkboxSelectionHeaderName: 'Selección',
                      booleanCellTrueLabel: 'sí',
                      booleanCellFalseLabel: 'no',
                      actionsCellMore: 'más',
                      pinToLeft: 'Anclar a la izquierda',
                      pinToRight: 'Anclar a la derecha',
                      unpin: 'Desanclar',
                      treeDataGroupingHeaderName: 'Grupo',
                      treeDataExpand: 'ver hijos',
                      treeDataCollapse: 'ocultar hijos',
                      toolbarDensityLabel: 'Densidad',
                      toolbarDensityCompact: 'Compacta',
                      toolbarDensityStandard: 'Estándar',
                      toolbarDensityComfortable: 'Cómoda',
                      toolbarExportLabel: 'Exportar',
                      toolbarExportCSV: 'Descargar como CSV',
                      toolbarExportPrint: 'Imprimir',
                      // Textos para columnas
                      toolbarColumnsLabel: 'Seleccionar columnas',
                      columnsManagementSearchTitle: 'Buscar columna',
                      columnsManagementNoColumns: 'No hay columnas',
                      columnsManagementShowHideAllText: 'Mostrar/Ocultar todas',
                      columnsManagementReset: 'Restablecer'
                    }}
                  />

              {/* Botones superpuestos sobre la paginación */}
              <Button
                onClick={resetColumnVisibility}
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: 140,
                  minWidth: 'auto',
                  width: 'auto',
                  height: 32,
                  padding: '0 8px',
                  color: '#6c757d',
                  zIndex: 10,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '16px',
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    color: '#495057'
                  }
                }}
              >
                Restablecer columnas
              </Button>

              {/* Botón de exportar superpuesto sobre la paginación */}
              <Button
                onClick={() => {
                  const exportColumns = getExportableColumns();

                  const headerRow = exportColumns.map((column) => {
                    if (typeof column.headerName === 'string' && column.headerName.trim()) {
                      return column.headerName;
                    }
                    return column.field;
                  });

                  const dataRows = iniciativasParaRender.map((row) =>
                    exportColumns.map((column) => {
                      const rawValue = getExportCellValue(row, column.field);
                      return formatExportValueForCsv(rawValue);
                    })
                  );

                  const csvContent = [headerRow, ...dataRows]
                    .map((row) => row.map(escapeCsvCell).join(';'))
                    .join('\n');

                  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
                  const link = document.createElement('a');
                  link.href = URL.createObjectURL(blob);
                  link.download = `desarrollo_negocio_${new Date().toISOString().split('T')[0]}.csv`;
                  link.click();
                }}
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: 16,
                  minWidth: 'auto',
                  width: 'auto',
                  height: 32,
                  padding: '0 8px',
                  color: '#6c757d',
                  zIndex: 10,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '16px',
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    color: '#495057'
                  }
                }}
              >
                Exportar tabla
                <DownloadIcon sx={{ fontSize: 16, ml: 0.5 }} />
              </Button>
            </Box>
              </CardContent>
            </Card>
      </Box>
    </ThemeProvider>
  );
};

export default TablaDesarrolloNegocio;